import {
  AlertTriangle,
  Building2,
  CalendarClock,
  Headphones,
  Languages,
  Megaphone,
  PenLine,
  UserPlus,
} from 'lucide-react';
import type { CallAgent, CallAgentInput, CallFaq, CallTopic, AgentConfig } from './api/callsApi';
import { parseTopicsMarkdown, topicsToMarkdown } from './topicsMarkdown';

export type TemplateKey =
  | 'emergency'
  | 'assist'
  | 'overflow' // legacy key — pre-gallery Front-Desk Overflow drafts render as Assist
  | 'campaign'
  | 'newpatient'
  | 'spanish'
  | 'reschedule'
  | 'referral'
  | 'custom';

// Builder sections a template composes (rendered in this order when present).
export type SectionKey =
  | 'script'
  | 'routing'
  | 'handoff'
  | 'sources'
  | 'offer'
  | 'knowledge'
  | 'policy'
  | 'callbackLanguage'
  | 'alerts'
  | 'value';

// ── Template-specific FAQ builders ────────────────────────────────────────────
// "What are your hours?" / "Where are you located?" aren't seeded here — the
// backend auto-injects them into the knowledge base from live Location
// settings (see calls.service.ts syncFaqKnowledgeBase / withLocationFaqs).

function assistFaqs(): CallFaq[] {
  return [
    {
      q: 'When will someone call me back?',
      a: 'Our front desk returns calls within the hour during business hours. If you called near closing, you’ll hear from us first thing tomorrow.',
    },
    {
      q: 'Are you taking new patients?',
      a: 'Yes! We’re welcoming new patients and families. I can take your details right now and the team will call to set up your first visit.',
    },
    {
      q: "I'm running late for my appointment",
      a: 'Acknowledge, capture the appointment time and how late they’ll be, and send an instant note to the desk.',
    },
    { q: 'Where do I park?', a: '' },
  ];
}

function newPatientFaqs(): CallFaq[] {
  return [
    {
      q: 'What happens at the first visit?',
      a: 'Your first visit includes a comprehensive exam, X-rays, and a gentle cleaning if time allows. Plan for about an hour, and arrive 10 minutes early if you haven’t filled out our new-patient forms yet.',
    },
    {
      q: 'Do you take my insurance?',
      a: 'I’ll note your carrier and the team will verify your exact coverage before your visit.',
    },
  ];
}

function spanishFaqs(): CallFaq[] {
  return [
    {
      q: '¿Aceptan pacientes nuevos?',
      a: '¡Sí! Estamos aceptando pacientes nuevos y familias. Puedo tomar sus datos ahora mismo y el equipo le llamará para programar su primera visita.',
    },
    {
      q: '¿Cuánto cuesta una limpieza sin seguro?',
      a: 'Ofrecemos precios accesibles para pacientes sin seguro y opciones de financiamiento. El equipo le confirmará el costo exacto en su llamada de regreso.',
    },
  ];
}

const BASE_FAQS: CallFaq[] = [{ q: 'Do you take my insurance?', a: '' }];

// PRD defaults — practice-editable, summed as *opportunity*, never revenue.
export const DEFAULT_VALUE_TABLE: Record<string, number> = {
  'New patient — general / family': 950,
  'Pediatric (per child)': 600,
  'Implant consult': 4500,
  'Invisalign consult': 5000,
  'Cosmetic / veneers': 2200,
};

export const TRANSFERABLE_INTENT_OPTIONS = [
  { key: 'booking_request', label: 'Booking or changing appointments' },
  { key: 'billing_or_insurance', label: 'Billing & payment questions' },
  { key: 'existing_patient_callback', label: 'Complex existing-patient issues' },
  { key: 'caller_asks_for_person', label: 'Caller asks for a person' },
] as const;

// The only two languages Retell agents can be configured for — Retell's own
// locale codes, persisted verbatim in CallAgent.languages.
export const AGENT_LANGUAGE_OPTIONS = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'es-419', label: 'Spanish (Latin America)' },
] as const;

const VALID_AGENT_LANGUAGE_CODES: readonly string[] = AGENT_LANGUAGE_OPTIONS.map((o) => o.code);

export const PHONE_PROVIDERS = [
  'Traditional landline (AT&T, Frontier, cable voice…)',
  'Weave',
  'Mango Voice',
  'RingCentral',
  'Nextiva',
  'Comcast Business VoiceEdge',
  'Ooma Office',
  'Other / not sure',
] as const;

// Forwarding is configured on the practice's phone system, not by us — so
// these are honest, provider-level pointers, not verified per-carrier codes.
const HOSTED_STEPS = (portal: string) => [
  `In ${portal}, open call-routing / overflow rules for your main line.`,
  'Forward busy and unanswered calls (after your chosen ring time) to your tracked number.',
];
export const FORWARDING_STEPS: Record<(typeof PHONE_PROVIDERS)[number], string[]> = {
  'Traditional landline (AT&T, Frontier, cable voice…)': [
    'Most carriers use *90 (forward when busy) and *92 (forward when unanswered) followed by your tracked number — but codes vary by carrier and plan.',
    'If those codes don’t work, ask your carrier to enable "call forward busy / no answer" to your tracked number.',
  ],
  Weave: HOSTED_STEPS('the Weave admin portal'),
  'Mango Voice': HOSTED_STEPS('the Mango Voice admin portal'),
  RingCentral: HOSTED_STEPS('the RingCentral admin portal (Call Handling & Forwarding)'),
  Nextiva: HOSTED_STEPS('the Nextiva admin portal'),
  'Comcast Business VoiceEdge': HOSTED_STEPS('the VoiceEdge admin portal'),
  'Ooma Office': HOSTED_STEPS('the Ooma Office Manager'),
  'Other / not sure': [
    'Ask your phone provider to forward busy and unanswered calls to your tracked number.',
  ],
};

// ── Templates ─────────────────────────────────────────────────────────────────

export interface CallTemplate {
  id: TemplateKey;
  name: string;
  icon: typeof AlertTriangle;
  iconColor: string;
  iconBgClass: string;
  blurb: string;
  handles: string[];
  badge?: 'new' | 'specialists';
  /** Practice specialties that float this card to the top of the gallery. */
  specialistBoost?: string[];
  /** 'es' locks the language chip — single-language pipelines keep transcription reliable. */
  language: 'en' | 'es';
  maxInstances: number;
  sections: SectionKey[];
  defaults: {
    greeting: string;
    greetingGloss?: string; // English reference for Spanish content
    config: AgentConfig;
  };
}

export const TEMPLATES: CallTemplate[] = [
  {
    id: 'emergency',
    name: 'After-Hours Emergency',
    icon: AlertTriangle,
    iconColor: 'var(--error)',
    iconBgClass: 'templateIconEmergency',
    blurb:
      'Answers when closed. Runs your after-hours protocol, captures a callback, escalates to 911.',
    handles: ['After-hours calls', 'Your emergency protocol', 'Callback capture'],
    language: 'en',
    maxInstances: 1,
    sections: ['script', 'knowledge', 'alerts'],
    defaults: {
      greeting:
        'Thanks for calling. Our office is currently closed. I can pass an urgent message to the team — are you experiencing a life-threatening emergency?',
      config: { urgentAlertEnabled: true },
    },
  },
  {
    id: 'assist',
    name: 'Front-Desk Assist',
    icon: Headphones,
    iconColor: 'var(--info)',
    iconBgClass: 'templateIconOverflow',
    blurb:
      'Answers first or on overflow. Handles the quick stuff itself — hours, directions, running-late notes, prioritized callbacks — and hands callers to your front desk when they’re free.',
    handles: ['Pre-routing', 'Quick requests', 'Smart handoff', 'Callback queue'],
    language: 'en',
    maxInstances: 1,
    sections: ['script', 'routing', 'handoff', 'knowledge', 'alerts'],
    defaults: {
      greeting:
        "Thanks for calling {{practice_name}}! I'm the office assistant — how can I help you today?",
      config: {
        callbackPromise: 'within the hour during business hours',
        routingMode: 'assist_first',
        ringSeconds: 20,
        transferRingSeconds: 20,
        transferableIntents: TRANSFERABLE_INTENT_OPTIONS.map((o) => o.key),
        // Safe default until live-transfer audio is verified per line (PRD §5.1)
        captureOnlyMode: true,
        insuranceList: [],
        smsRecapEnabled: true,
        smsDirectionsEnabled: true,
        urgentAlertEnabled: true,
      },
    },
  },
  {
    id: 'campaign',
    name: 'Campaign Responder',
    icon: Megaphone,
    iconColor: 'var(--warning)',
    iconBgClass: 'templateIconCampaign',
    blurb:
      'A tracked number for every ad, mailer, or landing page — answered by an agent that knows the offer. Captures the lead, alerts you when a hot lead doesn’t book, and reports ROI per campaign.',
    handles: ['Offer questions', 'Lead capture', 'Source attribution', 'Hot-lead alerts'],
    language: 'en',
    maxInstances: 1,
    sections: ['sources', 'script', 'offer', 'knowledge', 'alerts', 'value'],
    defaults: {
      greeting: 'Thanks for calling {{practice_name}}! Are you calling about the {{offer_name}}?',
      config: {
        callbackPromise: 'within the hour during business hours',
        hotLeadAlertEnabled: true,
        smsRecapEnabled: true,
        urgentAlertEnabled: true,
        valueTable: { ...DEFAULT_VALUE_TABLE },
      },
    },
  },
  {
    id: 'newpatient',
    name: 'New-Patient Line',
    icon: UserPlus,
    iconColor: 'var(--success)',
    iconBgClass: 'templateIconNewPatient',
    blurb:
      'An always-on number for your website and Google profile that runs a full new-patient intake — contact details, insurance, what brings them in, how they found you — and gives them your forms link.',
    handles: ['Full intake', 'How-they-found-you', 'Forms link'],
    badge: 'new',
    language: 'en',
    maxInstances: 1,
    sections: ['script', 'knowledge', 'alerts', 'value'],
    defaults: {
      greeting:
        "Thanks for calling {{practice_name}} — you've reached our new-patient line, and I can get everything set up for your first visit.",
      config: {
        callbackPromise: 'within the hour during business hours',
        smsRecapEnabled: true,
        urgentAlertEnabled: true,
        valueTable: { ...DEFAULT_VALUE_TABLE },
        formsLink: '',
      },
    },
  },
  {
    id: 'spanish',
    name: 'Línea en Español',
    icon: Languages,
    iconColor: 'var(--warning)',
    iconBgClass: 'templateIconSpanish',
    blurb:
      'A dedicated Spanish line on its own number — the whole call in Spanish, end to end. You review everything in English, and summary emails arrive in English, with the full Spanish transcript in the Call Log.',
    handles: ['Spanish end-to-end', 'English review', 'Callback language'],
    badge: 'new',
    language: 'es',
    maxInstances: 1,
    sections: ['script', 'callbackLanguage', 'knowledge', 'alerts'],
    defaults: {
      greeting:
        'Gracias por llamar a {{practice_name}}. Habla la asistente de la oficina — ¿me puede decir si llama por una emergencia o por dolor dental fuerte?',
      greetingGloss:
        '"Thanks for calling {{practice_name}}. This is the office assistant — can you tell me if you’re calling about an emergency or severe dental pain?"',
      config: {
        callbackPromise: 'dentro de una hora durante horas hábiles',
        spanishCallbackStaff: false,
        smsRecapEnabled: true,
        urgentAlertEnabled: true,
      },
    },
  },
  {
    id: 'reschedule',
    name: 'Reschedule & Cancellation Saver',
    icon: CalendarClock,
    iconColor: 'var(--accent)',
    iconBgClass: 'templateIconReschedule',
    blurb:
      'Handles “I need to change my appointment” calls. Offers to move the visit instead of canceling, and flags same-day cancellations instantly so you can fill the slot.',
    handles: ['Save the visit', 'Same-day alerts', 'Menu option or forward'],
    badge: 'new',
    language: 'en',
    maxInstances: 1,
    sections: ['script', 'policy', 'alerts'],
    defaults: {
      greeting:
        'Thanks for calling {{practice_name}} — I can help change or reschedule an appointment.',
      config: {
        saveAttemptEnabled: true,
        saveLine:
          'I completely understand. Before I cancel it — would moving it to a different day work better? I can have the team hold a time that fits.',
        cancellationPolicy:
          "We ask for 24 hours' notice for changes when possible, so we can offer the time to another patient.",
        sameDayAlertEnabled: true,
        smsRecapEnabled: true,
        urgentAlertEnabled: true,
      },
    },
  },
  {
    id: 'referral',
    name: 'Referring-Office Line',
    icon: Building2,
    iconColor: 'var(--info)',
    iconBgClass: 'templateIconReferral',
    blurb:
      'For endodontic, perio, and oral-surgery practices: a dedicated line for referring offices that captures the complete referral — patient, tooth, procedure, urgency — and emails a structured referral confirmation to your front desk.',
    handles: ['GP referrals', 'Referral confirmation', 'Emergency flag'],
    badge: 'specialists',
    specialistBoost: ['endodontics', 'periodontics', 'oral_surgery', 'orthodontics'],
    language: 'en',
    maxInstances: 1,
    sections: ['script', 'alerts'],
    defaults: {
      greeting:
        'Thanks for calling the referral line for {{practice_name}} — I can take the full referral right now.',
      config: {
        confirmBothOffices: true,
        emergencyReferralAlertEnabled: true,
        // Referral details include patient information — email only, by design
        smsRecapEnabled: false,
        urgentAlertEnabled: true,
        procedures: ['Root canal therapy', 'Retreatment', 'Apicoectomy', 'Evaluation'],
      },
    },
  },
  {
    id: 'custom',
    name: 'Start from scratch',
    icon: PenLine,
    iconColor: 'var(--text-secondary)',
    iconBgClass: 'templateIconBlank',
    blurb:
      'A blank agent with the full builder — write your own script, knowledge, and capture list. Safety rules stay locked on.',
    handles: ['Blank script', 'Full builder'],
    language: 'en',
    maxInstances: 3,
    sections: ['script', 'routing', 'knowledge', 'alerts'],
    defaults: {
      greeting: '',
      config: { smsRecapEnabled: true, urgentAlertEnabled: true },
    },
  },
];

// Legacy 'overflow' drafts (pre-gallery) resolve to the Assist template.
export function resolveTemplate(key: string): CallTemplate | undefined {
  const effective = key === 'overflow' ? 'assist' : key;
  return TEMPLATES.find((t) => t.id === effective);
}

function templateFaqs(templateId: TemplateKey): CallFaq[] {
  switch (templateId === 'overflow' ? 'assist' : templateId) {
    case 'emergency':
      // Emergency's Q&A lives in draft.topics, from the practice-neutral
      // defaults served by GET /calls/templates/emergency/topics (backend
      // templateDefaults.ts; see makeDraft below) — no {q,a} defaults here.
      return [];
    case 'assist':
      return assistFaqs();
    case 'newpatient':
      return newPatientFaqs();
    case 'spanish':
      return spanishFaqs();
    default:
      return BASE_FAQS.map((f) => ({ ...f }));
  }
}

// ── Draft state ───────────────────────────────────────────────────────────────

export interface DraftAgent {
  id?: string;
  name: string;
  templateKey: TemplateKey;
  greeting: string;
  hours: string;
  address: string;
  faqs: CallFaq[];
  // Emergency template only — edited as a single markdown blob (so content
  // authored elsewhere, e.g. by an LLM, can be pasted in one shot) and
  // parsed into CallTopic[] for topicsJson at save time (draftToInput).
  topicsMarkdown: string;
  notifyEmails: string[];
  transferNumber: string;
  doctorLastName: string;
  languages: string[];
  config: AgentConfig;
  areaCode: string;
  source: string;
  status?: string;
  trackingNumber?: string | null;
  fallbackNumber?: string | null;
  isProtected?: boolean;
}

export interface Prefill {
  hours: string;
  address: string;
  languages: string[];
  areaCode: string;
}

// US area code from a location phone ("(512) 555-0100", "+1 512…"), or ''
// when it can't be read — the user then picks one before a number is bought.
export function areaCodeFromPhone(phone: string | null | undefined): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return national.length === 10 ? national.slice(0, 3) : '';
}

export function isValidAreaCode(areaCode: string): boolean {
  return /^[2-9]\d{2}$/.test(areaCode);
}

export function makeDraft(
  templateId: TemplateKey,
  prefill?: Prefill,
  emergencyTopics?: CallTopic[],
): DraftAgent {
  const t = resolveTemplate(templateId);
  const hours = prefill?.hours ?? '';
  const address = prefill?.address ?? '';
  const languages =
    t?.language === 'es' ? ['es-419'] : prefill?.languages?.length ? prefill.languages : ['en-US'];

  return {
    name: t?.id === 'custom' ? '' : (t?.name ?? ''),
    templateKey: templateId,
    greeting: t?.defaults.greeting ?? '',
    hours,
    address,
    faqs: templateFaqs(templateId),
    topicsMarkdown: templateId === 'emergency' ? topicsToMarkdown(emergencyTopics ?? []) : '',
    notifyEmails: [],
    transferNumber: '',
    doctorLastName: '',
    languages,
    config: { ...(t?.defaults.config ?? {}) },
    areaCode: prefill?.areaCode ?? '',
    source: '',
  };
}

export function agentToDraft(a: CallAgent): DraftAgent {
  // Drop any legacy display-string values (e.g. "English", "Español") that
  // predate the locale-code migration — the UI only ever shows/sends the
  // fixed en-US/es-419 codes, so a stale value must not linger unseen and
  // get re-submitted alongside a newly picked language on save.
  const validLanguages = Array.isArray(a.languages)
    ? a.languages.filter((l) => VALID_AGENT_LANGUAGE_CODES.includes(l))
    : [];

  return {
    id: a.id,
    name: a.name,
    templateKey: a.templateKey,
    greeting: a.greeting,
    hours: a.hours,
    address: a.address,
    faqs: Array.isArray(a.faqsJson) ? a.faqsJson : [],
    topicsMarkdown: topicsToMarkdown(Array.isArray(a.topicsJson) ? a.topicsJson : []),
    notifyEmails: Array.isArray(a.notifyEmails) ? a.notifyEmails : [],
    transferNumber: a.transferNumber ?? '',
    doctorLastName: a.doctorLastName ?? '',
    languages: validLanguages.length ? validLanguages : ['en-US'],
    config: {
      ...(resolveTemplate(a.templateKey)?.defaults.config ?? {}),
      ...(a.configJson ?? {}),
    },
    areaCode: a.areaCode,
    source: a.source ?? '',
    status: a.status,
    trackingNumber: a.trackingNumber,
    fallbackNumber: a.fallbackNumber,
    isProtected: a.isProtected,
  };
}

export function draftToInput(draft: DraftAgent, locationId: string): CallAgentInput {
  return {
    locationId,
    name: draft.name,
    templateKey: draft.templateKey,
    greeting: draft.greeting,
    faqsJson: draft.faqs,
    topicsJson: parseTopicsMarkdown(draft.topicsMarkdown).topics,
    notifyEmails: draft.notifyEmails,
    transferNumber: draft.transferNumber || null,
    doctorLastName: draft.doctorLastName,
    languages: draft.languages,
    configJson: draft.config,
    areaCode: draft.areaCode,
    source: draft.source || null,
  };
}

// Router state handed from the new-agent builder to AgentDetailPage when the
// user chose "Approve & activate" on an unsaved draft.
export interface AutoActivateState {
  autoActivate: true;
  existingPhoneNumber?: string;
}
